// Контракт `modules.*` — карта доступности модулей для клиента (ТЗ §6.4).
//
// ┌─ Что клиент получает и чего он НЕ получает ───────────────────────────────┐
// │ Отдаём ПОСЧИТАННУЮ карту `{ id, state, reason, nav }`.                     │
// │ НЕ отдаём правила: `percentage`, `cohorts`, `tiers` из `ModuleRollout`     │
// │ наружу не уходят. Процент раскатки в бандле — это (а) утечка продуктовых   │
// │ планов, (б) приглашение подобрать себе бакет подменой userId на клиенте.   │
// │ Резолвер живёт на сервере, клиент получает ответ, а не входные данные.     │
// └───────────────────────────────────────────────────────────────────────────┘

import { z } from "zod";
import { MODULE_IDS } from "../../module/ids.js";
import { moduleRolloutSchema } from "../../module/rollout.js";
import { moduleUnavailableReasonSchema } from "../_shared/errors.js";

export const moduleAvailabilityStateSchema = z.enum(["available", "teaser", "read_only", "hidden"]);
export type ModuleAvailabilityState = z.infer<typeof moduleAvailabilityStateSchema>;

/** Как поверхность обязана показать пункт (единственная реализация — `navVisibilityFor`). */
export const moduleNavPresentationSchema = z.enum([
	"visible",
	"locked",
	"outage",
	"external-outage",
	"hidden",
]);

export const moduleAvailabilityEntrySchema = z.object({
	id: z.enum(MODULE_IDS),
	state: moduleAvailabilityStateSchema,
	/** Отсутствует ровно для `state: "available"`. */
	reason: moduleUnavailableReasonSchema.optional(),
	/** Готовое решение для навигации — чтобы шесть поверхностей не переоткрывали матрицу §6.1. */
	nav: moduleNavPresentationSchema,
});
export type ModuleAvailabilityEntry = z.infer<typeof moduleAvailabilityEntrySchema>;

export const moduleAvailabilityInputSchema = z
	.object({
		/**
		 * Платформа клиента. По умолчанию `web`: Capacitor обязан прислать своё
		 * значение явно, иначе гейт `platform_unsupported` не сработает.
		 */
		platform: z.enum(["web", "capacitor"]).default("web"),
	})
	.optional();

export const moduleAvailabilityOutputSchema = z.object({
	modules: z.array(moduleAvailabilityEntrySchema),
	/**
	 * Поколение карты. Растёт при каждом изменении раскатки; клиент кладёт его
	 * рядом со снапшотом в `idb-keyval` и отбрасывает ответ, пришедший из
	 * прошлой жизни (асинхронный ответ обязан проверять актуальность мира,
	 * куда возвращается).
	 */
	version: z.number().int().nonnegative(),
	/**
	 * Кому посчитана карта. Обязательное поле, а не удобство: снапшот кладётся
	 * в общее хранилище устройства, и при смене пользователя карта прошлого
	 * («залогинен» ≠ «залогинен кем») обязана быть отброшена по несовпадению id.
	 */
	userId: z.string(),
	/** Сколько секунд снапшот считается свежим без рефетча. */
	ttlSeconds: z.number().int().positive(),
	/**
	 * Показывать ли коммерцию: тарифы, апгрейд, покупку.
	 *
	 * Едет тем же каналом, что и карта модулей, по двум причинам. Первая —
	 * ответ обязан пережить офлайн, а офлайн-снапшот уже есть только у этого
	 * канала. Вторая — признак посчитан ДЛЯ КОНКРЕТНОГО пользователя и
	 * платформы и лежит рядом с `userId`, поэтому при смене аккаунта
	 * отбрасывается тем же сравнением, что и разделы (инцидент `40b62b3b`).
	 *
	 * Отдельный запрос за этим флагом означал бы второй кэш, второй срок
	 * жизни и второе правило сброса — то есть третью копию условия показа.
	 */
	commerceVisible: z.boolean(),
});
export type ModuleAvailabilityOutput = z.infer<typeof moduleAvailabilityOutputSchema>;

// ── Админская сторона: раскатка (`admin.modules.*`) ──────────────────────
//
// Живёт здесь, а не в `contracts/admin/schemas.ts`, из-за направления
// импортов: admin-контракт — источник `KILLSWITCH_OPS` для резолвера, и
// обратная стрелка `admin → module/rollout` замкнула бы цикл. Схемы админских
// процедур модульной платформы естественно принадлежат модульной платформе;
// `AdminRouterContract` импортирует их отсюда.
//
// Здесь, в отличие от `modules.availability`, правила отдаются КАК ЕСТЬ:
// админу нужен именно процент и когорты, чтобы их редактировать. Разница
// аудиторий и есть причина двух разных процедур.

export const adminModuleRowSchema = z.object({
	id: z.enum(MODULE_IDS),
	kind: z.string(),
	lifecycle: z.string(),
	/** Раскатка из `admin_settings.modules`; `null` — админ её не трогал. */
	rollout: moduleRolloutSchema.nullable(),
	/** Что применяется, когда записи нет (выводится из `lifecycle` манифеста). */
	defaultRollout: moduleRolloutSchema,
	/** Тарифный гейт манифеста — единственный источник `teaser`. */
	minTier: z.enum(["free", "plus", "pro"]).nullable(),
	/** Аварийный выключатель модуля (`KILLSWITCH_OPS`), если объявлен. */
	killswitchOp: z.string().nullable(),
	/** Модули, без которых этот не работает (`requires.modules` + `requires.meta`). */
	dependsOn: z.array(z.enum(MODULE_IDS)),
});
export type AdminModuleRow = z.infer<typeof adminModuleRowSchema>;

export const listAdminModulesOutput = z.object({
	items: z.array(adminModuleRowSchema),
	/** Поколение карты — растёт на каждое изменение раскатки. */
	version: z.number().int().nonnegative(),
});

export const setModuleRolloutInput = z.object({
	moduleId: z.enum(MODULE_IDS),
	/**
	 * `null` — снять переопределение и вернуться к дефолту по `lifecycle`.
	 * Отдельное значение, а не `{ state: "on" }`: «включён по дефолту» и
	 * «админ явно включил» — разные факты, и второй не должен переживать смену
	 * дефолта в манифесте молча.
	 */
	rollout: moduleRolloutSchema.nullable(),
	/** Причина изменения — уходит в `admin_audit_logs`. */
	reason: z.string().min(3).max(500),
});

export const setModuleRolloutOutput = z.object({
	ok: z.literal(true),
	version: z.number().int().nonnegative(),
});
